/**
 * ENG-10641 (ADR-0035, amended): the Docker memory ceiling of an isolated
 * agent's container follows its `agent_class`. Coding (`advanced`) agents get a
 * 6g ceiling, everyone else 2g (see `AGENT_CLASS_CONTAINER_MEMORY` in core).
 *
 * Two paths set the ceiling, and they MUST resolve it identically:
 *
 *  - spawn: `buildDockerRunCommand` passes `--memory` / `--memory-swap`;
 *  - live:  `syncContainerMemory` runs `docker update` on a running container
 *    when the class changes, so a promotion takes effect without a respawn.
 *
 * `resolveContainerMemory` is the one resolver both call. If the two ever
 * diverged on the operator override, the first manager tick after a deploy would
 * "correct" every container on a host running `AGT_ISOLATION_MEMORY=6g` back down
 * to 2g — the OOM this exists to fix, applied fleet-wide by the fix.
 */
import { containerMemoryForAgentClass } from '@augmented/core';
import { hardBoundedExecFile, type BoundedExecResult } from './bounded-exec.js';

/**
 * Docker's documented `--memory` syntax: a positive integer with an optional
 * `b`, `k`, `m` or `g` suffix. Deliberately stricter than
 * `parseDockerMemoryBytes`, which also reads what `docker inspect` and older
 * configs produce: an override must be something `docker run` is documented to
 * accept, because the spawn path hands it over verbatim.
 */
const DOCKER_MEMORY_OVERRIDE = /^[1-9]\d*[bkmg]?$/i;

/**
 * Docker rejects any `--memory` below 6 MiB ("Minimum memory limit allowed is
 * 6MB", `linuxMinMemory` in the daemon), so a syntactically valid `5m` would
 * break `docker run` exactly like `lots` does.
 */
const DOCKER_MIN_MEMORY_BYTES = 6 * 1024 ** 2;

/** The operator override, when it is set AND valid; otherwise null. */
function validMemoryOverride(): string | null {
  const raw = process.env.AGT_ISOLATION_MEMORY?.trim();
  if (!raw || !DOCKER_MEMORY_OVERRIDE.test(raw)) return null;
  const bytes = parseDockerMemoryBytes(raw);
  return bytes !== null && bytes >= DOCKER_MIN_MEMORY_BYTES ? raw : null;
}

/**
 * True when `AGT_ISOLATION_MEMORY` is set but is not a value Docker accepts, so
 * callers can say why it is being ignored.
 */
export function isInvalidMemoryOverride(): boolean {
  const raw = process.env.AGT_ISOLATION_MEMORY?.trim();
  return !!raw && validMemoryOverride() === null;
}

/**
 * The ceiling for an agent's container. A valid `AGT_ISOLATION_MEMORY` stays the
 * operator override and wins over the class, as it did before classes existed.
 *
 * An INVALID override (`lots`, `1.5g`) is ignored and the class value is used.
 * Passed through, it would put `--memory lots` on the `docker run` line: Docker
 * rejects it and the isolated agent can never spawn. Before classes there was no
 * safe value to fall back to; now every class has one.
 */
export function resolveContainerMemory(agentClass: unknown): string {
  return validMemoryOverride() ?? containerMemoryForAgentClass(agentClass);
}

const UNIT_BYTES: Record<string, number> = {
  '': 1,
  k: 1024,
  m: 1024 ** 2,
  g: 1024 ** 3,
  t: 1024 ** 4,
};

/**
 * Parse Docker's memory size syntax (`2g`, `512m`, `6GB`, `1.5g`, `6442450944`)
 * into bytes, the unit `docker inspect` reports. Docker's units are binary
 * whether or not an `i` is written. Null for anything unparseable, so a typo in
 * the operator override is reported rather than compared as if it were a number.
 */
export function parseDockerMemoryBytes(value: string): number | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*([kmgt]?)(?:i?b)?\s*$/i.exec(value);
  if (!m) return null;
  const bytes = Math.floor(Number(m[1]) * UNIT_BYTES[m[2]!.toLowerCase()]!);
  return Number.isSafeInteger(bytes) && bytes > 0 ? bytes : null;
}

export type ContainerMemorySyncOutcome =
  | 'unchanged'
  | 'raised'
  /** The new ceiling is lower. Left for the next respawn — see below. */
  | 'deferred-lower'
  | 'failed';

export interface ContainerMemorySyncDeps {
  exec: (args: string[]) => BoundedExecResult;
  log: (msg: string) => void;
}

const defaultDeps = (log: (msg: string) => void): ContainerMemorySyncDeps => ({
  exec: (args) => hardBoundedExecFile('docker', args, { timeoutMs: 15_000 }),
  log,
});

/** Bytes last known to be the container's ceiling, per agent code name. */
const appliedBytesBySession = new Map<string, number>();
/** Last message logged per agent, so a repeating outcome logs once, not per tick. */
const lastLogBySession = new Map<string, string>();

function logOnce(deps: ContainerMemorySyncDeps, codeName: string, msg: string): void {
  if (lastLogBySession.get(codeName) === msg) return;
  lastLogBySession.set(codeName, msg);
  deps.log(msg);
}

/**
 * Record the ceiling a container was just spawned with, so the first healthy
 * tick does not re-inspect a value the manager set itself moments ago.
 */
export function seedContainerMemory(codeName: string, memory: string): void {
  const bytes = parseDockerMemoryBytes(memory);
  if (bytes === null) appliedBytesBySession.delete(codeName);
  else appliedBytesBySession.set(codeName, bytes);
  lastLogBySession.delete(codeName);
}

/** Test seam. */
export function resetContainerMemorySyncForTests(): void {
  appliedBytesBySession.clear();
  lastLogBySession.clear();
}

/**
 * Bring a RUNNING isolated agent's container ceiling up to date with its class.
 * Called on every healthy tick; cheap when nothing changed (a map read).
 *
 * UNKNOWN BASELINE — the manager restarted and the container survived, so the map
 * is empty. The value is read back with `docker inspect` rather than assumed.
 * This is also how a host converges when an operator removes a stale
 * `AGT_ISOLATION_MEMORY`: no respawn needed for a raise.
 *
 * ONLY RAISES ARE APPLIED LIVE. On cgroup v2, lowering `memory.max` below what the
 * container is using does not fail; the kernel reclaims and then OOM-kills inside
 * the container. Demoting a coding agent mid-build would kill the build it is
 * running — or claude itself. A lower ceiling is therefore logged and left for the
 * next respawn, which starts the container at the new value.
 *
 * FAIL-OPEN. A failed inspect or update is logged (once per distinct message) and
 * retried on the next tick. It never kills or respawns the session: the agent keeps
 * running at its current ceiling, which is where it was before the class changed.
 */
export function syncContainerMemory(
  codeName: string,
  agentClass: unknown,
  log: (msg: string) => void,
  deps: ContainerMemorySyncDeps = defaultDeps(log),
): ContainerMemorySyncOutcome {
  const desired = resolveContainerMemory(agentClass);
  const desiredBytes = parseDockerMemoryBytes(desired);
  const container = `agt-${codeName}`;
  if (isInvalidMemoryOverride()) {
    logOnce(
      deps,
      `${codeName}:override`,
      `[isolation-memory] AGT_ISOLATION_MEMORY='${process.env.AGT_ISOLATION_MEMORY}' is not a valid ` +
        `Docker memory size (integer with optional b/k/m/g, at least 6m) — ignored; using the agent_class ceiling ${desired}`,
    );
  }
  if (desiredBytes === null) {
    logOnce(deps, codeName, `[isolation-memory] '${codeName}': unparseable memory ceiling '${desired}' — not applied`);
    return 'failed';
  }

  let currentBytes = appliedBytesBySession.get(codeName);
  if (currentBytes === undefined) {
    const inspected = deps.exec(['inspect', '-f', '{{.HostConfig.Memory}}', container]);
    const parsed = inspected.kind === 'ok' ? Number(inspected.stdout.trim()) : NaN;
    if (!Number.isSafeInteger(parsed) || parsed < 0) {
      logOnce(
        deps,
        codeName,
        `[isolation-memory] '${codeName}': could not read ${container}'s memory ceiling ` +
          `(${describe(inspected)}) — will retry`,
      );
      return 'failed';
    }
    // 0 is Docker's "no limit". Treat it as larger than any ceiling, so it is
    // never lowered live (see the docblock), only at the next respawn.
    currentBytes = parsed === 0 ? Number.POSITIVE_INFINITY : parsed;
    appliedBytesBySession.set(codeName, currentBytes);
  }

  if (currentBytes === desiredBytes) {
    lastLogBySession.delete(codeName);
    return 'unchanged';
  }
  if (currentBytes > desiredBytes) {
    logOnce(
      deps,
      codeName,
      `[isolation-memory] '${codeName}': ceiling ${formatBytes(currentBytes)} -> ${desired} ` +
        `(agent_class=${String(agentClass)}) is a decrease — deferred to the next respawn, ` +
        `not applied live (lowering memory.max under use OOM-kills inside the container)`,
    );
    return 'deferred-lower';
  }

  // `--memory-swap` equal to `--memory` keeps ENG-10369's no-swap invariant. Docker
  // also refuses a `--memory` above the container's existing `--memory-swap`, so a
  // raise without it would fail on every container this manager has spawned.
  const updated = deps.exec(['update', '--memory', desired, '--memory-swap', desired, container]);
  if (updated.kind !== 'ok') {
    logOnce(
      deps,
      codeName,
      `[isolation-memory] '${codeName}': docker update to ${desired} failed (${describe(updated)}) — will retry`,
    );
    return 'failed';
  }
  appliedBytesBySession.set(codeName, desiredBytes);
  lastLogBySession.delete(codeName);
  deps.log(
    `[isolation-memory] '${codeName}': ceiling ${formatBytes(currentBytes)} -> ${desired} ` +
      `(agent_class=${String(agentClass)}), applied live`,
  );
  return 'raised';
}

function describe(r: BoundedExecResult): string {
  if (r.kind === 'ok') return `unexpected output '${r.stdout.trim().slice(0, 80)}'`;
  if (r.kind === 'timed-out') return 'timed out';
  return r.message.trim().slice(0, 200) || `exit ${r.status}`;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return 'unlimited';
  const gib = bytes / 1024 ** 3;
  return Number.isInteger(gib) ? `${gib}g` : `${Math.round(bytes / 1024 ** 2)}m`;
}
